import type {
  AgenticPlatformAskUserField,
  AgenticPlatformAskUserQuestion,
} from 'agentic-platform-schema';

import { isDefined, isTruthy } from 'common';

/** What the user entered for each field, keyed by field id. */
export type AgenticChatQuestionAnswers = Map<string, string[]>;

function getAgenticChatQuestionFieldAnswerText(options: {
  answers: string[];
  field: AgenticPlatformAskUserField;
}): string {
  const { answers, field } = options;
  if (field.kind === 'shortText' || field.kind === 'longText') {
    // Whitespace is not an answer: a field typed into and then cleared has been left blank.
    return answers.join('\n').trim();
  }
  // The labels read better in the transcript than values the model is free to make opaque.
  return field.choices
    .filter((choice) => answers.includes(choice.value))
    .map((choice) => choice.label)
    .join(', ');
}

/**
 * The reply to an agent's question, as the message the user is deemed to have sent.
 *
 * The platform prompts the agent with the trailing user message, so the answer travels as prose.
 */
export function formatAgenticChatQuestionAnswer(options: {
  answers: AgenticChatQuestionAnswers;
  question: AgenticPlatformAskUserQuestion;
}): string {
  const { answers, question } = options;
  const answeredFields = question.fields
    .map((field) => ({
      field,
      text: getAgenticChatQuestionFieldAnswerText({ answers: answers.get(field.id) ?? [], field }),
    }))
    .filter((answeredField) => isTruthy(answeredField.text.length));
  const singleField = answeredFields.length === 1 ? answeredFields[0] : undefined;
  if (question.fields.length === 1 && isDefined(singleField)) {
    return singleField.text;
  }
  return answeredFields
    .map((answeredField) => `${answeredField.field.label}: ${answeredField.text}`)
    .join('\n');
}
